import React, { useMemo, useState } from 'react';
import { Box, Chip, IconButton, Collapse, Badge, Typography, TextField } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { layoutCardRadius } from '../theme/colors';

const ProductsTagFilters = ({
  setTagFilter,
  tagFilter,
  tags,
  numProducts,
  defaultExpanded = false,
  sx,
}) => {
  const [showFilters, setShowFilters] = useState(defaultExpanded);
  const [chipSearch, setChipSearch] = useState('');

  const selectedIds = useMemo(
    () => new Set(tagFilter.map((tag) => tag.id)),
    [tagFilter]
  );

  const visibleTags = useMemo(() => {
    const query = chipSearch.trim().toLowerCase();

    const isVisible = (tag) =>
      selectedIds.has(tag.id) ||
      !query ||
      tag.name.toLowerCase().includes(query);

    const selected = tagFilter
      .map((tag) => tags.find((catalogTag) => catalogTag.id === tag.id))
      .filter(Boolean)
      .filter(isVisible);

    const unselected = tags.filter(
      (tag) => !selectedIds.has(tag.id) && isVisible(tag)
    );

    return [...selected, ...unselected];
  }, [tags, chipSearch, selectedIds, tagFilter]);

  const isTagSelected = (tag) => selectedIds.has(tag.id);

  const handleTagClick = (tag) => {
    if (isTagSelected(tag)) {
      setTagFilter(tagFilter.filter((selectedTag) => selectedTag.id !== tag.id));
      return;
    }

    setTagFilter([...tagFilter, tag]);
  };

  if (!tags.length) {
    return (
      <Box sx={{ alignSelf: 'stretch', ...sx }}>
        <Typography variant="body2" color="text.secondary">
          {numProducts} {numProducts === 1 ? 'item' : 'itens'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch', gap: 1, ...sx }}>
      <Box
        title={`Filtros de tags`}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', cursor: 'pointer' }}
        onClick={() => setShowFilters(!showFilters)}
      >
        <Typography variant="body2">
          {numProducts} {numProducts === 1 ? 'item' : 'itens'}
        </Typography>
        <Badge badgeContent={tagFilter.length} color="primary" sx={{ ml: 1 }}>
          <IconButton size="small" aria-label="Filtrar por tags">
            <FilterList />
          </IconButton>
        </Badge>
      </Box>

      <Collapse in={showFilters}>
        <TextField
          size="small"
          placeholder="Buscar tag..."
          value={chipSearch}
          onChange={(event) => setChipSearch(event.target.value)}
          fullWidth
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: layoutCardRadius,
            },
          }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start' }}>
          {visibleTags.map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              variant={isTagSelected(tag) ? 'contained' : 'outlined'}
              color={isTagSelected(tag) ? 'primary' : 'secondary'}
              onClick={() => handleTagClick(tag)}
            />
          ))}
          {visibleTags.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Nenhuma tag encontrada.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ProductsTagFilters;
